import { useState, useEffect } from "react"
import { CampaignTypeSelection } from "./campaign-type-selection"
import { useNavigate, Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { 
  PlusCircle, 
  Search, 
  AlertCircle, 
  LayoutIcon, 
  ChevronDown,
  ChevronRight,
  Sparkles,
  Zap,
  PencilIcon,
  Brain as BrainIcon,
  FileText,
  Calendar,
  Edit,
  Loader2,
  ArrowUpDown
} from "lucide-react"
import { supabase } from "@/lib/supabase"
import { useBrands } from "@/lib/BrandsProvider"
import NoBrandSelected from "../NoBrandSelected"
import { PageHeader } from "@/components/ui/page-header"
import { LoadingSpinner } from "@/components/ui/loading-spinner"
import { Badge } from "@/components/ui/badge"
import { Card } from "@/components/ui/card"
import { useAnalytics } from '@/lib/analytics'
import { TimeWithTimezone, TimezoneContext } from '@/components/timezone'
import { ContentList } from './ContentList'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { toast } from "@/components/ui/use-toast"
import { Input } from "../ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import campaignBackgroundImage from "@/assets/images/campaign_background.png"

// Add type definitions
type Campaign = {
  id: string
  name: string
  status: 'Active' | 'Inactive'
  created_at: string
  campaign_type: string
  is_primary_brand_campaign: boolean
  content?: string | null
  call_to_action?: string | null
  originating_convergence_id?: string | null
  brand_id: string
  clicks?: number
  views?: number
  metadata?: Record<string, any> | null
  custom_idea?: string | null
  image_url?: string | null
}

type SortOption = 'date_desc' | 'date_asc' | 'name_asc' | 'name_desc'

type Convergence = {
  id: string
  trend: string
  name: string
}

type Content = {
  id: string
  name: string
  content_type: string
  created_at: string
}

export default function CampaignsScreen() {
  const navigate = useNavigate()
  const { selectedBrand } = useBrands()
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isTypeSelectionOpen, setIsTypeSelectionOpen] = useState(false)
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set())
  const [searchTerm, setSearchTerm] = useState('')
  const [sortOption, setSortOption] = useState<SortOption>('date_desc')
  const [campaignContents, setCampaignContents] = useState<Record<string, Content[]>>({})
  const [campaignContentCounts, setCampaignContentCounts] = useState<Record<string, number>>({})
  const [convergences, setConvergences] = useState<Record<string, Convergence>>({})
  const [showEditThemeDialog, setShowEditThemeDialog] = useState(false)
  const [editingCampaignId, setEditingCampaignId] = useState<string | null>(null)
  const [centralTheme, setCentralTheme] = useState('')
  const [savingTheme, setSavingTheme] = useState(false)
  const [generatingAutoContent, setGeneratingAutoContent] = useState<string | null>(null)
  const [showGenerateDialog, setShowGenerateDialog] = useState(false)
  const [selectedCampaignForGenerate, setSelectedCampaignForGenerate] = useState<Campaign | null>(null)
  const { trackEvent, events } = useAnalytics()

  const toggleCardExpansion = (campaignId: string) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev)
      if (newSet.has(campaignId)) {
        newSet.delete(campaignId)
      } else {
        // Close all other cards when opening a new one for better focus
        newSet.clear()
        newSet.add(campaignId)
        // Fetch content and convergence data when expanding
        fetchCampaignContent(campaignId)
        const campaign = campaigns.find(c => c.id === campaignId)
        if (campaign?.originating_convergence_id && !convergences[campaign.originating_convergence_id]) {
          fetchConvergence(campaign.originating_convergence_id)
        }
      }
      return newSet
    })
  }

  const handleNewCampaign = async (isPrimaryBrandCampaign = false) => {
    // Track campaign creation attempt
    trackEvent(events.FEATURE_USED, {
      feature: 'campaign_creation_attempted',
      is_primary_brand_campaign: isPrimaryBrandCampaign,
      brand_id: selectedBrand?.id
    })
    
    // Track campaign type selection opened
    trackEvent(events.FEATURE_USED, {
      feature: 'campaign_type_selection_opened',
      is_primary_brand_campaign: isPrimaryBrandCampaign,
      brand_id: selectedBrand?.id
    })
    setIsTypeSelectionOpen(true)
  }

  const fetchCampaignContent = async (campaignId: string) => {
    try {
      const { data, error } = await supabase
        .from('contents')
        .select('*')
        .eq('status', 'READY')
        .eq('campaign_id', campaignId)
      
      if (error) throw error
      setCampaignContents(prev => ({ ...prev, [campaignId]: (data as Content[]) || [] }))
    } catch (err) {
      console.error('Error fetching campaign content:', err)
    }
  }

  const fetchAllCampaignContentCounts = async (campaignIds: string[]) => {
    try {
      const counts: Record<string, number> = {}
      
      // Fetch content counts for all campaigns in a single query
      const { data, error } = await supabase
        .from('contents')
        .select('campaign_id')
        .eq('status', 'READY')
        .in('campaign_id', campaignIds)
      
      if (error) throw error
      
      // Count content pieces per campaign
      campaignIds.forEach(id => {
        counts[id] = data?.filter(content => content.campaign_id === id).length || 0
      })
      
      setCampaignContentCounts(counts)
    } catch (err) {
      console.error('Error fetching campaign content counts:', err)
    }
  }

  const fetchConvergence = async (convergenceId: string) => {
    try {
      const { data, error } = await supabase
        .from('convergences')
        .select('id, trend, name')
        .eq('id', convergenceId)
        .single()
      
      if (error) throw error
      if (data) {
        setConvergences(prev => ({ ...prev, [convergenceId]: data as Convergence }))
      }
    } catch (err) {
      console.error('Error fetching convergence:', err)
    }
  }

  const handleCreateContent = (campaignId: string) => {
    navigate('/content/create', { state: { campaignId } })
  }

  const handleShowGenerateDialog = (campaign: Campaign) => {
    setSelectedCampaignForGenerate(campaign)
    setShowGenerateDialog(true)
  }

  const handleGenerateAutoContent = async () => {
    if (!selectedCampaignForGenerate) return
    setShowGenerateDialog(false)
    setGeneratingAutoContent(selectedCampaignForGenerate.id)
    
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        throw new Error('No authentication session found')
      }

      const workerUrl = import.meta.env.VITE_WORKERS_URL
      const response = await fetch(`${workerUrl}/rainfactory/manual-autoposter/generate-autopost-content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          campaignId: selectedCampaignForGenerate.id,
          convergenceId: selectedCampaignForGenerate.originating_convergence_id
        }),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`)
      }

      if (result.success) {
        toast({
          title: "Content Generated Successfully!",
          description: result.message,
        })
        
        // Refresh the content list for this campaign
        fetchCampaignContent(selectedCampaignForGenerate.id)
      } else {
        throw new Error(result.error || 'Content generation failed')
      }
    } catch (error) {
      console.error('Error generating auto content:', error)
      toast({
        title: "Generation Failed",
        description: error instanceof Error ? error.message : 'Failed to generate content',
        variant: "destructive",
      })
    } finally {
      setGeneratingAutoContent(null)
      setSelectedCampaignForGenerate(null)
    }
  }

  const handleEditTheme = (campaign: Campaign) => {
    setEditingCampaignId(campaign.id)
    setCentralTheme(campaign.metadata?.central_theme || campaign.custom_idea || '')
    setShowEditThemeDialog(true)
  }

  const handleSaveCentralTheme = async () => {
    if (!editingCampaignId) return
    
    const campaign = campaigns.find(c => c.id === editingCampaignId)
    if (!campaign) return
    
    setSavingTheme(true)
    try {
      const metadata = { 
        ...campaign.metadata,
        central_theme: centralTheme 
      }
      
      const { error } = await supabase
        .from('campaigns')
        .update({ metadata })
        .eq('id', editingCampaignId)
      
      if (error) throw error
      
      // Update local campaign state
      setCampaigns(prev => prev.map(c => c.id === editingCampaignId ? {
        ...c,
        metadata
      } : c))
      
      toast({
        title: "Theme updated",
        description: "The campaign theme has been updated successfully.",
      })
      
      setShowEditThemeDialog(false)
      setEditingCampaignId(null)
    } catch (err) {
      console.error('Error updating theme:', err)
      toast({
        title: "Update failed",
        description: "There was a problem updating the campaign theme.",
        variant: "destructive",
      })
    } finally {
      setSavingTheme(false)
    }
  }

  useEffect(() => {
    async function fetchCampaigns() {
      if (!selectedBrand) return;

      try {
        setIsLoading(true)
        
        // Track campaigns page view
        trackEvent(events.PAGE_VIEWED, {
          page: 'campaigns',
          brand_id: selectedBrand.id
        })
        
        const { data, error } = await supabase
          .from('campaigns')
          .select('*, image_url')
          .eq('brand_id', selectedBrand.id)
          .order('created_at', { ascending: false })

        if (error) throw error

        // Track campaigns loaded
        trackEvent(events.FEATURE_USED, {
          feature: 'campaigns_list',
          count: data?.length || 0,
          has_primary_campaign: data?.some(c => c.is_primary_brand_campaign) || false,
          brand_id: selectedBrand.id
        })
        
        const campaignsData = (data as Campaign[]) || []
        setCampaigns(campaignsData)
        
        // Fetch content counts for all campaigns
        if (campaignsData.length > 0) {
          const campaignIds = campaignsData.map(c => c.id)
          fetchAllCampaignContentCounts(campaignIds)
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch campaigns';
        setError(errorMessage)
        
        // Track error
        trackEvent('error_occurred', {
          error_type: 'campaign_fetch_error',
          error_message: errorMessage,
          brand_id: selectedBrand.id
        })
      } finally {
        setIsLoading(false)
      }
    }

    fetchCampaigns()
  }, [selectedBrand, trackEvent, events])

  const hasPrimaryBrandCampaign = campaigns.some(campaign => {
    return campaign.is_primary_brand_campaign || campaign.campaign_type === 'BASE'
  })

  // Filter campaigns based on search term
  const filteredCampaigns = campaigns.filter(campaign =>
    campaign.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Sort filtered campaigns based on sort option
  const sortedCampaigns = [...filteredCampaigns].sort((a, b) => {
    switch (sortOption) {
      case 'date_desc':
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      case 'date_asc':
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      case 'name_asc':
        return a.name.localeCompare(b.name)
      case 'name_desc':
        return b.name.localeCompare(a.name)
      default:
        return 0
    }
  })

  // Helper functions for styling and content
  const getCampaignIcon = (campaign: Campaign) => {
    switch (campaign.campaign_type) {
      case 'CONVERGENCE_BASED':
        return <Sparkles className="h-5 w-5" />
      case 'CUSTOM':
        return <Zap className="h-5 w-5" />
      case 'BASE':
        return <BrainIcon className="h-5 w-5" />
      default:
        return <LayoutIcon className="h-5 w-5" />
    }
  }

  const getCampaignTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'CONVERGENCE_BASED': 'Convergence Campaign',
      'BASE': 'Brand Campaign',
      'CUSTOM': 'Custom Campaign'
    }
    return labels[type] || type
  }

  const getCampaignGradient = (campaign: Campaign) => {
    switch (campaign.campaign_type) {
      case 'CONVERGENCE_BASED':
        return 'from-purple-50 to-violet-50 dark:from-purple-950/20 dark:to-violet-950/20'
      case 'CUSTOM':
        return 'from-orange-50 to-red-50 dark:from-orange-950/20 dark:to-red-950/20'
      case 'BASE':
        return 'from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20'
      default:
        return 'from-gray-50 to-slate-50 dark:from-gray-950/20 dark:to-slate-950/20'
    }
  }

  const getCampaignIconGradient = (campaign: Campaign) => {
    switch (campaign.campaign_type) {
      case 'CONVERGENCE_BASED':
        return 'from-purple-500 to-violet-600'
      case 'CUSTOM':
        return 'from-orange-500 to-red-600'
      case 'BASE':
        return 'from-blue-500 to-indigo-600'
      default:
        return 'from-gray-500 to-slate-600'
    }
  }

  const getCampaignTextColor = (campaign: Campaign) => {
    switch (campaign.campaign_type) {
      case 'CONVERGENCE_BASED':
        return 'text-purple-900 dark:text-purple-100'
      case 'CUSTOM':
        return 'text-orange-900 dark:text-orange-100'
      case 'BASE':
        return 'text-blue-900 dark:text-blue-100'
      default:
        return 'text-gray-900 dark:text-gray-100'
    }
  }

  const getCampaignSubtextColor = (campaign: Campaign) => {
    switch (campaign.campaign_type) {
      case 'CONVERGENCE_BASED':
        return 'text-purple-700 dark:text-purple-200'
      case 'CUSTOM':
        return 'text-orange-700 dark:text-orange-200'
      case 'BASE':
        return 'text-blue-700 dark:text-blue-200'
      default:
        return 'text-gray-700 dark:text-gray-200'
    }
  }

  const getCampaignBottomGradient = (campaign: Campaign) => {
    switch (campaign.campaign_type) {
      case 'CONVERGENCE_BASED':
        return 'from-purple-200 via-violet-200 to-indigo-200 dark:from-purple-800 dark:via-violet-800 dark:to-indigo-800'
      case 'CUSTOM':
        return 'from-orange-200 via-red-200 to-pink-200 dark:from-orange-800 dark:via-red-800 dark:to-pink-800'
      case 'BASE':
        return 'from-blue-200 via-indigo-200 to-purple-200 dark:from-blue-800 dark:via-indigo-800 dark:to-purple-800'
      default:
        return 'from-gray-200 via-slate-200 to-zinc-200 dark:from-gray-800 dark:via-slate-800 dark:to-zinc-800'
    }
  }

  if (!selectedBrand) {
    return <NoBrandSelected />
  }

  if (error) {
    return (
      <div className="p-6">
        <PageHeader
          title="Campaigns"
          description="Campaigns are designed to make different types of content work together seamlessly to align all of the content within the campaign."
          actions={
            <Button
              variant="default"
              onClick={() => handleNewCampaign(!hasPrimaryBrandCampaign)}
            >
              <PlusCircle className="mr-2 h-4 w-4" />
              New Campaign
            </Button>
          }
        />
        <div className="mt-4 flex items-center gap-2 text-destructive">
          <AlertCircle className="h-4 w-4" />
          <span>Error loading campaigns: {error}</span>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Enhanced Header Section with Gradient Background */}
      <div className="relative overflow-hidden bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/40 rounded-2xl border border-blue-200/50 shadow-sm p-8">
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
        <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-blue-200/20 to-transparent rounded-full -translate-y-20 translate-x-20" />
        <div className="relative">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg">
                <LayoutIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 bg-clip-text text-transparent mb-2">Campaigns</h1>
                <p className="text-gray-600 text-lg leading-relaxed">
                  Campaigns are designed to make different types of content work together seamlessly to align all of the content within the campaign.
                </p>
              </div>
            </div>
            <Button 
              onClick={() => handleNewCampaign(!hasPrimaryBrandCampaign)}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-sm hover:shadow-lg transition-all duration-300 transform hover:scale-105 border-0"
            >
              <PlusCircle className="h-4 w-4 mr-2" />
              {!hasPrimaryBrandCampaign ? "Create Brand Campaign" : "New Campaign"}
            </Button>
          </div>
        </div>
      </div>

      {/* Timezone Context */}
      <TimezoneContext 
        placement="inline" 
        compact={true}
        className="mb-4"
      />

      {/* Search and Sort Bar */}
      <div className="bg-white rounded-lg border shadow-sm p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search campaigns..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={sortOption} onValueChange={(value: SortOption) => setSortOption(value)}>
            <SelectTrigger className="w-full sm:w-[200px]">
              <ArrowUpDown className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="date_desc">Newest First</SelectItem>
              <SelectItem value="date_asc">Oldest First</SelectItem>
              <SelectItem value="name_asc">Name (A-Z)</SelectItem>
              <SelectItem value="name_desc">Name (Z-A)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Campaign Cards - Consistent 2-column grid layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : sortedCampaigns.length > 0 ? (
          sortedCampaigns.map((campaign) => {
            const isExpanded = expandedCards.has(campaign.id)
            const contentCount = campaignContents[campaign.id]?.length || campaignContentCounts[campaign.id] || 0
            const convergence = campaign.originating_convergence_id ? convergences[campaign.originating_convergence_id] : null
            
            return (
              <Card key={campaign.id} className="bg-white rounded-lg border shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md">
                {/* Featured Image/Placeholder Section */}
                <div className="aspect-video w-full relative overflow-hidden">
                  {campaign.image_url ? (
                    <img 
                      src={campaign.image_url} 
                      alt={`${campaign.name} campaign`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <img 
                      src={campaignBackgroundImage} 
                      alt={`${campaign.name} campaign background`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  )}
                </div>
                
                {/* Card Header - Always Visible with mobile-optimized padding */}
                <div className={`p-3 sm:p-5 border-b bg-gradient-to-r ${getCampaignGradient(campaign)} cursor-pointer hover:bg-opacity-80 transition-all duration-200`}
                     onClick={() => toggleCardExpansion(campaign.id)}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {/* Campaign Type Icon - Hidden on mobile to save space */}
                      <div className={`hidden sm:flex w-12 h-12 bg-gradient-to-br ${getCampaignIconGradient(campaign)} rounded-xl items-center justify-center shadow-lg flex-shrink-0 ring-2 ring-white/20`}>
                        <div className="w-6 h-6 text-white">
                          {getCampaignIcon(campaign)}
                        </div>
                      </div>
                      
                      {/* Campaign Information */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-2.5 mb-2">
                          <h3 className={`text-lg sm:text-xl font-bold ${getCampaignTextColor(campaign)} leading-tight`}>
                            {campaign.name}
                          </h3>
                          {campaign.is_primary_brand_campaign && (
                            <Badge variant="secondary" className="text-xs font-semibold flex-shrink-0 px-2 py-1 w-fit">Primary</Badge>
                          )}
                        </div>
                        
                        {/* Campaign Metadata - Mobile-optimized Layout */}
                        <div className="space-y-2">
                          {/* Mobile: Stack vertically, Desktop: Keep horizontal */}
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
                            <Badge variant="outline" className="text-xs font-medium px-2 py-1 w-fit">
                              {getCampaignTypeLabel(campaign.campaign_type)}
                            </Badge>
                            <div className="flex items-center gap-1.5">
                              <div className={`w-2 h-2 rounded-full ${campaign.status === 'Active' ? 'bg-green-500' : 'bg-yellow-500'}`} />
                              <span className={`text-xs font-medium capitalize ${campaign.status === 'Active' ? 'text-green-700 dark:text-green-300' : 'text-yellow-700 dark:text-yellow-300'}`}>
                                {campaign.status}
                              </span>
                            </div>
                          </div>
                          
                          {/* Mobile: Stack vertically for better readability */}
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 text-xs">
                            <div className={`${getCampaignSubtextColor(campaign)} flex items-center gap-1.5 font-medium`}>
                              <FileText className="h-3.5 w-3.5" />
                              <span>{contentCount} content{contentCount !== 1 ? '' : ''}</span>
                            </div>
                            <div className={`${getCampaignSubtextColor(campaign)} flex items-center gap-1.5`}>
                              <Calendar className="h-3.5 w-3.5" />
                              <TimeWithTimezone 
                                date={campaign.created_at} 
                                dateOnly={false}
                                size="xs"
                                showTimezone={false}
                                showTooltip={true}
                                showRelative={true}
                                relativeMode="contextual"
                                relativeContext="compact"
                                preferRelative={true}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Action Buttons - Mobile optimized */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        className="hidden sm:flex"
                        onClick={(e) => {
                          e.stopPropagation()
                          trackEvent(events.CAMPAIGN_EDITED, {
                            action: 'navigate_to_edit',
                            campaign_id: campaign.id,
                            campaign_name: campaign.name,
                            campaign_type: campaign.campaign_type
                          })
                          navigate(`/campaigns/${campaign.id}/edit`)
                        }}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      {/* Mobile: Show only icon */}
                      <Button
                        variant="outline"
                        size="sm"
                        className="sm:hidden p-2"
                        onClick={(e) => {
                          e.stopPropagation()
                          trackEvent(events.CAMPAIGN_EDITED, {
                            action: 'navigate_to_edit',
                            campaign_id: campaign.id,
                            campaign_name: campaign.name,
                            campaign_type: campaign.campaign_type
                          })
                          navigate(`/campaigns/${campaign.id}/edit`)
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      {isExpanded ? (
                        <ChevronDown className="h-5 w-5 text-muted-foreground transition-transform" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-muted-foreground transition-transform" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="p-6 space-y-6 animate-in slide-in-from-top-2 duration-300">
                    {/* Campaign Details */}
                    <div className="bg-muted/30 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-base flex items-center gap-2">
                          {getCampaignIcon(campaign)}
                          Campaign Details
                        </h4>
                      </div>
                      
                      {campaign.campaign_type === 'CUSTOM' && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <h5 className="text-sm font-medium text-muted-foreground">Central Theme</h5>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleEditTheme(campaign)}
                            >
                              <PencilIcon className="h-4 w-4 mr-1" /> Edit
                            </Button>
                          </div>
                          <p className="text-sm">
                            {campaign.metadata?.central_theme || campaign.custom_idea || 'No theme specified'}
                          </p>
                        </div>
                      )}
                      
                      {campaign.campaign_type === 'CONVERGENCE_BASED' && convergence && (
                        <div>
                          <h5 className="text-sm font-medium text-muted-foreground mb-2">Based on Convergence</h5>
                          <Link 
                            to={`/convergences/${convergence.id}`}
                            className="text-sm text-primary hover:underline flex items-center"
                          >
                            <Sparkles className="h-4 w-4 mr-1" />
                            {convergence.trend}
                          </Link>
                        </div>
                      )}
                      
                      {campaign.campaign_type === 'BASE' && (
                        <div>
                          <p className="text-sm text-muted-foreground mb-2">
                            This is a base brand campaign built on your brand's strategy and positioning. You can use the Brand Genius tool to help control the content.
                          </p>
                          <Link 
                            to="/brand-genius"
                            className="text-sm text-primary hover:underline flex items-center"
                          >
                            <BrainIcon className="h-4 w-4 mr-1" />
                            Visit Brand Genius
                          </Link>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-center gap-4">
                      <Button 
                        onClick={() => handleCreateContent(campaign.id)} 
                        size="lg"
                      >
                        <PlusCircle className="h-4 w-4 mr-2" />
                        Create Content
                      </Button>
                      
                      {campaign.campaign_type === 'CONVERGENCE_BASED' && campaign.originating_convergence_id && (
                        <Button 
                          onClick={() => handleShowGenerateDialog(campaign)} 
                          size="lg" 
                          variant="outline"
                          disabled={generatingAutoContent === campaign.id}
                        >
                          {generatingAutoContent === campaign.id ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Sparkles className="h-4 w-4 mr-2" />
                          )}
                          {generatingAutoContent === campaign.id ? 'Generating...' : 'Generate Content'}
                        </Button>
                      )}
                    </div>

                    {/* Content List */}
                    <div className="bg-white rounded-lg border shadow-sm p-4">
                      <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Content in this campaign
                      </h4>
                      <ContentList campaignId={campaign.id} />
                    </div>
                  </div>
                )}
                
                <div className={`h-2 bg-gradient-to-r ${getCampaignBottomGradient(campaign)}`} />
              </Card>
            )
          })
        ) : (
          <div className="relative overflow-hidden bg-gradient-to-br from-white via-gray-50/30 to-blue-50/20 rounded-2xl border border-gray-200/50 shadow-sm">
            <div className="absolute inset-0 bg-grid-pattern opacity-5" />
            <div className="relative flex flex-col items-center justify-center py-20 px-8">
              <div 
                className="group relative overflow-hidden w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-full flex items-center justify-center mb-6 cursor-pointer hover:from-blue-200 hover:to-indigo-300 transition-all duration-300 transform hover:scale-110 hover:shadow-lg"
                onClick={() => handleNewCampaign(!hasPrimaryBrandCampaign)}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-full" />
                <PlusCircle className="relative h-12 w-12 text-blue-600 group-hover:text-blue-700 transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-2">
                {searchTerm ? "No campaigns match your search" : "No campaigns found"}
              </h3>
              <p className="text-gray-600 text-center mb-6 max-w-md leading-relaxed">
                {searchTerm ? (
                  `Try adjusting your search terms or filters`
                ) : (
                  <>
                    {!hasPrimaryBrandCampaign ? (
                      "Get started by creating your brand's primary campaign - it's complimentary!"
                    ) : (
                      <>
                        Get started by creating a new campaign or find a{' '}
                        <Link to="/convergences" className="text-blue-600 hover:text-blue-700 underline underline-offset-4">
                          convergence
                        </Link>
                        {' '}you like and turn it into a campaign
                      </>
                    )}
                  </>
                )}
              </p>
              {!searchTerm && (
                <Button
                  onClick={() => handleNewCampaign(!hasPrimaryBrandCampaign)}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-sm hover:shadow-lg transition-all duration-300 transform hover:scale-105 border-0"
                >
                  <PlusCircle className="h-4 w-4 mr-2" />
                  {!hasPrimaryBrandCampaign ? "Create Brand Campaign" : "Create Your First Campaign"}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      <CampaignTypeSelection
        isOpen={isTypeSelectionOpen}
        onClose={() => setIsTypeSelectionOpen(false)}
        isPrimaryBrandCampaign={!hasPrimaryBrandCampaign}
      />

      {/* Generate Content Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate Campaign Content</DialogTitle>
            <DialogDescription>
              This will generate content for your convergence-based campaign.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Content will be generated based on the convergence trend and scheduled for posting according to your platform settings.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleGenerateAutoContent}>
              Generate Content
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Theme Dialog */}
      <Dialog open={showEditThemeDialog} onOpenChange={setShowEditThemeDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Campaign Theme</DialogTitle>
            <DialogDescription>
              Update the central theme for this custom campaign.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="centralTheme">Central Theme</Label>
              <Textarea
                id="centralTheme"
                value={centralTheme}
                onChange={(e) => setCentralTheme(e.target.value)}
                placeholder="Describe the central theme or idea for this campaign"
                className="min-h-[100px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditThemeDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveCentralTheme} disabled={savingTheme}>
              {savingTheme ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
